import { IconDot } from "@/assets/icons/geist/IconDot";
import type { Size } from "@/assets/icons/geist/Size";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { type PlainEnvironment } from "@/queries/environment-queries";
import { effectiveState } from "@/routes/environments/phase";
import { statusText } from "@/utils/environment";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useMemo, type FC } from "react";

type EnvironmentStatusDotProps = {
    env: PlainEnvironment;
    size: Size;
} & PropsWithClassName;

export const EnvironmentStatusDot: FC<EnvironmentStatusDotProps> = ({ env, size, className }) => {
    const state = useMemo(() => effectiveState(env), [env]);
    const tooltip = useMemo(() => statusText(state), [state]);

    const { color } = useMemo(() => {
        let color = "text-content-tertiary size-2 m-2";

        switch (state.state) {
            case EnvironmentPhase.RUNNING:
                color = "text-content-green";
                break;
            case EnvironmentPhase.UPDATING:
            case EnvironmentPhase.STARTING:
            case EnvironmentPhase.CREATING:
            case EnvironmentPhase.STOPPING:
            case EnvironmentPhase.DELETING:
                color = "text-content-orange";
                break;
            case EnvironmentPhase.STOPPED:
                if (state.failures) {
                    color = "text-content-red";
                } else {
                    color = "text-content-tertiary size-2 m-2";
                }
                break;
        }
        return {
            color,
            ...state,
        };
    }, [state]);

    return (
        <Tooltip content={tooltip}>
            <IconDot size={size} className={cn("block", color, className)} />
        </Tooltip>
    );
};
